import React, { FormEvent, useCallback, useMemo } from 'react'
import { FormProvider, useForm } from 'react-hook-form'
import { useSelector } from 'react-redux'

import { usePartnerGetAllAllQuery, Company, Partner } from '../../redux/api/web-api'
import { isSupportDomain } from '../../../lib/currentDomain'
import { RootState } from '../../../support/redux/store'
import useText from '../../hooks/useText'
import { useCompanyQuery } from '../../hooks/useFetchEndpoints'
import { ChargerProducts, getChargerProductModelName } from '../../enums/Products'
import type { ChargePointStatus } from '../../redux/api/web-api'

import FilterDropdownItems from './filter-dropdown-items'
import SearchWithFilter from './search-input'

export type ChargePointPageFilterStatus = {
    companyId?: string
    id?: string
    imsiNumber?: string
    installedFirmwareVersion?: string
    name?: string
    ocppOnly?: boolean
    ownerEmail?: string
    ownerMobile?: string
    ownerName?: string
    noOfItems?: number
    page?: number
    productId?: number
    partnerId?: number
    status?: ChargePointStatus
    column?: string
    reversed?: boolean
}

export type FilterValue = {
    key: string
    value: string | boolean | number
}

export type DecoratedFilterValue = {
    label?: string
    labelValue?: string | null
} & FilterValue

export const initialValues = {
    companyId: '',
    id: '',
    imsiNumber: '',
    installedFirmwareVersion: '',
    name: '',
    ocppOnly: undefined,
    ownerName: '',
    ownerEmail: '',
    ownerMobile: '',
    partnerId: undefined,
    productId: undefined,
    status: '',
} as unknown as Partial<ChargePointPageFilterStatus>

export type ChargePointsSearchWidgetProps = {
    onSubmit: (values: Partial<ChargePointPageFilterStatus>) => void
    isLoading?: boolean
    filter: Partial<ChargePointPageFilterStatus>
}

const ignoredFilterValues = ['id', 'page', 'noOfItems']

export function ChargePointsSearchWidget({
    onSubmit,
    isLoading,
    filter,
}: ChargePointsSearchWidgetProps): React.ReactElement {
    const text = useText()
    const { data: partnerData } = usePartnerGetAllAllQuery({ excludeOrphans: false }, { skip: !isSupportDomain })

    const searchInputFieldName = 'id'

    const methods = useForm<ChargePointPageFilterStatus>({
        defaultValues: filter,
        mode: 'onChange',
    })

    const { register, handleSubmit, reset, setValue, getValues } = methods

    const clear = (filterName?: string) => {
        const formValues = getValues()

        const valuesToSet = filterName
            ? {
                  ...formValues,
                  [filterName]: undefined,
              }
            : initialValues

        reset(
            {
                ...valuesToSet,
                id: formValues.id,
            },
            { keepDefaultValues: false, keepValues: false }
        )

        searchWithFilters({ ...valuesToSet, id: formValues.id }) // Used in order to keep search text input when clear filter is clicked
    }

    const partnerId = useSelector((state: RootState) => state.user.partnerId) ?? 0
    const { data: companies } = useCompanyQuery(partnerId)
    const companyOptions = useMemo<Company[] | undefined>(
        () =>
            companies?.map((company) => ({
                ...company,
                partnerId: company.partnerId ?? undefined,
            })),
        [companies]
    )

    const searchWithFilters = (formValues: Partial<ChargePointPageFilterStatus>) => {
        const filter = Object.entries(formValues).reduce((prev, [key, value]) => {
            return (typeof value === 'string' && value.length > 0) ||
                ((key === 'partnerId' || key === 'productId') && value !== undefined) ||
                key === 'ocppOnly'
                ? { ...prev, [key]: value }
                : prev
        }, {}) as Partial<ChargePointPageFilterStatus>

        onSubmit(filter)
    }

    const decorateFilterValues = useCallback(
        (
            filter: Partial<ChargePointPageFilterStatus>,
            companies?: Company[],
            partnerData?: Partner[]
        ): DecoratedFilterValue[] =>
            Object.entries(filter)
                .filter(([key, value]) => !ignoredFilterValues.includes(key) && value !== undefined)
                .sort((a, b) => a[0].localeCompare(b[0]))
                .map((nextFilter) => {
                    const filterLabelProps: FilterValue = {
                        key: nextFilter[0],
                        value: nextFilter[1],
                    }
                    switch (filterLabelProps.key) {
                        case 'companyId':
                            return {
                                ...filterLabelProps,
                                label: text('lblOrganisation'),
                                labelValue: companies?.find((company) => company.id === filterLabelProps.value)?.name,
                            }
                        case 'imsiNumber': {
                            return {
                                ...filterLabelProps,
                                label: text('lblImsiNumber'),
                            }
                        }
                        case 'installedFirmwareVersion': {
                            return {
                                ...filterLabelProps,
                                label: text('lblFirmwareVersion'),
                            }
                        }
                        case 'productId':
                            return {
                                ...filterLabelProps,
                                label: text('lblProduct'),
                                labelValue:
                                    typeof filterLabelProps.value === 'number'
                                        ? getChargerProductModelName(
                                              filterLabelProps.value,
                                              ChargerProducts[filterLabelProps.value]
                                          )
                                        : undefined,
                            }
                        case 'partnerId':
                            return {
                                ...filterLabelProps,
                                label: text('lblPartnerField'),
                                labelValue: partnerData?.find((partner) => partner.id === filterLabelProps.value)?.name,
                            }
                        case 'ocppOnly':
                            return {
                                ...filterLabelProps,
                                label: filterLabelProps.value ? text('lblOcpp') : text('lblCapiField'),
                            }
                        case 'ownerEmail':
                            return {
                                ...filterLabelProps,
                                label: text('lblOwnerEmail'),
                            }
                        case 'ownerName':
                            return {
                                ...filterLabelProps,
                                label: text('lblOwnerName'),
                            }
                        case 'ownerMobile':
                            return {
                                ...filterLabelProps,
                                label: text('lblOwnerMobile'),
                            }
                        default:
                            return {
                                ...filterLabelProps,
                            }
                    }
                }),
        [text]
    )

    const decoratedFilterValues = useMemo(
        () => decorateFilterValues(filter, companyOptions, partnerData ?? undefined),
        [decorateFilterValues, filter, companyOptions, partnerData]
    )

    return (
        <SearchWithFilter
            placeholder={text('lblChargePointId')}
            activeFilters={decoratedFilterValues}
            filterDropdownItems={
                <FormProvider {...methods}>
                    <FilterDropdownItems companies={companyOptions} partnerData={partnerData ?? undefined} />
                </FormProvider>
            }
            onClear={clear}
            onSubmit={handleSubmit(searchWithFilters)}
            {...register(searchInputFieldName)}
            onChange={(val: FormEvent<HTMLInputElement>) => {
                if (!('data' in val.nativeEvent)) {
                    setValue(searchInputFieldName, '')
                    handleSubmit(searchWithFilters)()
                } else {
                    setValue(searchInputFieldName, val.currentTarget.value)
                }
            }}
            disabled={isLoading}
        />
    )
}
